import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { GlobalsService } from './globals.service';

@Injectable({
	providedIn: 'root'
})
export class ComponentGuidanceService {
	private routeToComponentMap: { [key: string]: string } = {
		'/customers/view': 'CustomerView',
		'/customers': 'CustomersList',
		'/inventory/view': 'InventoryView',
		'/inventory/lookup': 'InventoryLookup',
		'/orders/entry': 'OrderEntry',
		'/orders/pick': 'OrderPick',
		'/orders/check': 'OrderCheck',
		'/orders/invoice': 'OrderInvoice',
		'/purchasing': 'Purchasing',
		'/dashboard': 'Dashboard',
		'/warehouse': 'Warehouse',
		'/receivables': 'Receivables'
	};

	constructor(private http: HttpClient, private globalsService: GlobalsService) {}

	getComponentFromRoute(route: string): string {
		if (!route) {return 'unknown';}

		const cleanRoute = route.split('?')[0].split('#')[0];
		
		for (const [routePattern, componentName] of Object.entries(this.routeToComponentMap)) {
			if (cleanRoute.startsWith(routePattern)) {
				return componentName;
			}
		}

		const routeParts = cleanRoute.split('/').filter(p => p);
		if (routeParts.length > 0) {
			return this.capitalizeFirst(routeParts[routeParts.length - 1]);
		}

		return 'unknown';
	}

	getComponentManual(componentName: string): Observable<string> {
		if (!componentName || componentName === 'unknown') {
			return of('');
		}

		const manualPath = `manuals/${componentName.toLowerCase()}.md`;
		return this.http.get(manualPath, { responseType: 'text' }).pipe(
			map(content => content || ''),
			catchError(() => of(''))
		);
	}

	getComponentFeatures(componentName: string): string[] {
		const featuresMap: { [key: string]: string[] } = {
			'CustomerView': [
				'View customer profile',
				'Manage transactions',
				'View order history',
				'Manage contacts',
				'Payment processing'
			],
			'InventoryView': [
				'View item details',
				'Check stock levels',
				'View movement history',
				'Manage pricing',
				'Update descriptions'
			],
			'OrderEntry': [
				'Create new orders',
				'Add items to cart',
				'Apply discounts',
				'Process payments',
				'Submit orders'
			]
		};

		return featuresMap[componentName] || [];
	}

	getRelatedComponents(componentName: string): string[] {
		const relatedMap: { [key: string]: string[] } = {
			'CustomerView': ['CustomersList', 'OrderEntry', 'Receivables'],
			'InventoryView': ['InventoryLookup', 'Purchasing', 'Warehouse'],
			'OrderEntry': ['CustomerView', 'InventoryLookup', 'OrderPick']
		};

		return relatedMap[componentName] || [];
	}

	private capitalizeFirst(str: string): string {
		if (!str) {return '';}
		return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
	}
}
